import { FormContext, FormDefinitionContext, IFormDefinitionContext, IFormDefinitionProperty, LookupsContext, pascalToCameCasePropertyPath } from "@ngt/opms";
import { FC, PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import FormTranslationKeyContext from "../../../contexts/FormTranslationKeyContext";


export interface IFormTranslationProviderV2Props extends PropsWithChildren {
    formTranslationKey: string;
}

const FormTranslationProvider: FC<IFormTranslationProviderV2Props> = ({
    formTranslationKey,
    children
}) => {

    const { t } = useTranslation();

    const lookups = useContext(LookupsContext);
    const formDefinitionContext = useContext(FormDefinitionContext);
    const {validate} = useContext<any>(FormContext);

    useEffect(() => {
        if(validate) {
            validate()
        }
    }, [t, validate])

    const updatedFormDefinitionContext: IFormDefinitionContext = useMemo(() => ({
        ...formDefinitionContext,
        data: formDefinitionContext?.data ? {
            ...formDefinitionContext.data,
            name: t(`${formTranslationKey}.name`, { defaultValue: formDefinitionContext.data?.name }),
            instructions: t(`${formTranslationKey}.instructions`, { defaultValue: formDefinitionContext.data?.instructions }),
            patientInstructions: t(`${formTranslationKey}.patientInstructions`, { defaultValue: formDefinitionContext.data?.patientInstructions }),

            properties: (
                formDefinitionContext.data?.properties.map(property => {
                    const propertyPath = pascalToCameCasePropertyPath(property.propertyName);
                    const simple = t(`${formTranslationKey}.${propertyPath}.simple`, { defaultValue: property.label });
                    const detailed = property.label === property.detailedLabel ? simple : t(`${formTranslationKey}.${propertyPath}.detailed`, { defaultValue: property.detailedLabel });

                    return {
                        ...property,
                        label: simple,
                        detailedLabel: detailed
                    }
                })
            ),
            subformProperties: (
                formDefinitionContext?.data.subformProperties ? 
                    Object.keys(formDefinitionContext.data.subformProperties).reduce<{[index: string]: IFormDefinitionProperty[]}>((state, key) => {
                        state[key] = formDefinitionContext.data!.subformProperties[key].map(property => {
                            const propertyPath = pascalToCameCasePropertyPath(`${key}.${property.propertyName}`);
                            const simple = t(`${formTranslationKey}.${propertyPath}.simple`, { defaultValue: property.label });
                            const detailed = property.label === property.detailedLabel ? simple : t(`${formTranslationKey}.${propertyPath}.detailed`, { defaultValue: property.detailedLabel });

                            return {
                                ...property,
                                label: simple,
                                detailedLabel: detailed
                            }
                        });

                        return state;
                    }, {}) : {}
            )
        } : undefined
    }), [formDefinitionContext, t, pascalToCameCasePropertyPath]);

    const updatedLookups = useMemo(() => {
        return {
            ...lookups,
            data: lookups?.data?.map(lookup => {
                return {
                    ...lookup,
                    items: lookup.items?.map(item => {
                        const propertyPath = pascalToCameCasePropertyPath(lookup.propertyName);
                        const value = t(`${formTranslationKey}.${propertyPath}.lookup.${item.id}`, { defaultValue: item.value });

                        return {
                            ...item,
                            value
                        }
                    })
                }
            })
        }
    }, [formTranslationKey, lookups, t])
    
    return (
        <FormDefinitionContext.Provider value={updatedFormDefinitionContext}>
            <FormTranslationKeyContext.Provider value={formTranslationKey}>
                <LookupsContext.Provider value={updatedLookups}>
                    {children}
                </LookupsContext.Provider>
            </FormTranslationKeyContext.Provider>
        </FormDefinitionContext.Provider>
    )
}

export default FormTranslationProvider;